import { config } from '../../../config/config';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import type { CoverPicture } from './drawBackground';

const cache = new Map<string, Promise<CoverPicture>>();

/** True for an address on another origin: the picture then needs CORS headers to be read back. */
export function isCrossOrigin(url: string): boolean {
  if (/^(blob|data):/.test(url)) {
    return false;
  }
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * The address that the editor may draw (review fix), or null. The server stores the address of a
 * picture as a relative API path (`files/<uuid>/download`, `public/cover-backgrounds/<uuid>/image`);
 * it becomes an address of our API. A `blob:` or `data:` address is a picture of this browser (an
 * own upload of the public tool). Any other address (a foreign host in a crafted design link) is
 * never loaded, so a design cannot make the browser ask another host.
 */
export function coverPictureAddress(url: string): string | null {
  if (/^(blob|data):/.test(url)) {
    return url;
  }
  const full = getFullAPIFileURL(url);
  const apiBase = config.fymApiURL(true);
  if (full.startsWith(apiBase) && !full.slice(apiBase.length).startsWith('/')) {
    return full;
  }
  try {
    const resolved = new URL(full, window.location.href);
    return resolved.origin === window.location.origin &&
      resolved.pathname.startsWith(new URL(apiBase, window.location.href).pathname)
      ? resolved.href
      : null;
  } catch {
    return null;
  }
}

function loadImage(url: string): Promise<CoverPicture> {
  return new Promise<CoverPicture>((resolve, reject) => {
    const image = new Image();
    if (isCrossOrigin(url)) {
      // Without this, a picture of another host taints the canvas and the export fails.
      image.crossOrigin = 'anonymous';
    }
    image.decoding = 'async';
    image.onload = () => resolve({ source: image, width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error(`The picture did not load: ${url}`));
    image.src = url;
  });
}

/**
 * Loads the picture of a background, once per address. Only an address of `coverPictureAddress`
 * loads. A file of the artist comes from our own origin (`/api_api/files/...`). A picture of another host (the bank bucket) is asked with CORS
 * (`crossOrigin = 'anonymous'`): that host must send `Access-Control-Allow-Origin`, or the
 * picture does not load. A failed load leaves the cache, so a later try asks again.
 */
export function loadCoverPicture(stored: string): Promise<CoverPicture> {
  const url = coverPictureAddress(stored);
  if (url === null) {
    return Promise.reject(new Error('The picture is not on a Music24 address'));
  }
  const cached = cache.get(url);
  if (cached !== undefined) {
    return cached;
  }
  const loading = loadImage(url).catch((error: unknown) => {
    cache.delete(url);
    throw error;
  });
  cache.set(url, loading);
  return loading;
}
